import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/stores/appStore';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Search, 
  Users, 
  Save,
  Info,
  AlertTriangle
} from 'lucide-react';
import { currencySymbols } from '@/types';
import { format } from 'date-fns';

interface MemberForCollection {
  id: string;
  full_name: string;
  title?: string;
  dues_balance: number;
  credit_balance: number;
  is_selected: boolean;
  amount: string;
}

interface PaymentAllocation {
  backlog: number;
  current: number;
  prepay: number;
}

export default function DuesCollection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentOrganization, addPayment } = useAppStore();
  
  const [members, setMembers] = useState<MemberForCollection[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<MemberForCollection[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Payment details
  const [paymentDate, setPaymentDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'transfer' | 'check' | 'card'>('cash');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');

  const currencySymbol = currentOrganization?.currency 
    ? currencySymbols[currentOrganization.currency as keyof typeof currencySymbols] || '$'
    : '$';

  const monthlyDues = currentOrganization?.monthly_dues_amount || 0;

  useEffect(() => {
    fetchMembers();
  }, [currentOrganization]);

  useEffect(() => {
    const filtered = members.filter((m) =>
      m.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredMembers(filtered);
  }, [searchQuery, members]);

  const fetchMembers = async () => {
    if (!currentOrganization) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('members')
        .select('id, full_name, title, dues_balance, credit_balance')
        .eq('organization_id', currentOrganization.id)
        .eq('status', 'active')
        .order('full_name');

      if (error) throw error;

      const formattedMembers = (data || []).map((m) => ({
        ...m,
        is_selected: false,
        amount: '',
      }));

      setMembers(formattedMembers);
      setFilteredMembers(formattedMembers);
    } catch (error) {
      console.error('Error fetching members:', error);
      toast.error(t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  // FIX #8: Operate on member ID instead of array index so that
  // concurrent state updates and fast search-while-toggling can never
  // resolve to the wrong member.
  const toggleMember = (memberId: string) => {
    setMembers((prev) =>
      prev.map((m) => {
        if (m.id !== memberId) return m;
        const toggled = !m.is_selected;
        return {
          ...m,
          is_selected: toggled,
          // Auto-fill with monthly dues when selecting and field is empty
          amount: toggled && !m.amount ? monthlyDues.toString() : m.amount,
        };
      })
    );
  };

  // FIX #8 (continued): same ID-based approach for amount updates.
  const updateAmount = (memberId: string, amount: string) => {
    setMembers((prev) =>
      prev.map((m) => (m.id === memberId ? { ...m, amount } : m))
    );
  };

  // FIX #5: Apply existing credit_balance before computing what portion
  // of the incoming payment covers backlog vs current vs prepay.
  const calculateAllocation = (member: MemberForCollection): PaymentAllocation => {
    const amount = parseFloat(member.amount) || 0;

    // Effective backlog after netting off any existing credit
    const effectiveDuesBalance = Math.max(
      0,
      (member.dues_balance || 0) - (member.credit_balance || 0)
    );

    // FIX #6: Guard against monthlyDues === 0 so that unconfigured orgs
    // don't silently classify the full payment as "prepay".
    const monthlyAmount = monthlyDues > 0 ? monthlyDues : amount;

    let remaining = amount;
    const allocation: PaymentAllocation = { backlog: 0, current: 0, prepay: 0 };

    // First, cover any effective backlog
    if (effectiveDuesBalance > 0 && remaining > 0) {
      const backlogPayment = Math.min(effectiveDuesBalance, remaining);
      allocation.backlog = backlogPayment;
      remaining -= backlogPayment;
    }

    // Then, cover current month
    if (remaining > 0) {
      const currentPayment = Math.min(monthlyAmount, remaining);
      allocation.current = currentPayment;
      remaining -= currentPayment;
    }

    // Remainder is prepay
    if (remaining > 0) {
      allocation.prepay = remaining;
    }

    return allocation;
  };

  /**
   * FIX #3 (race condition): Attempt an atomic server-side balance update
   * via RPC. Falls back to a direct column update if the RPC is not yet
   * deployed, so the app degrades gracefully without breaking.
   *
   * The previous implementation used `.then().catch()` chained directly on
   * `supabase.rpc(...)`, which returns a `PromiseLike` (Supabase's
   * `PostgrestFilterBuilder`), not a native `Promise`. `PromiseLike` only
   * guarantees `.then()` — it does NOT have `.catch()`, causing TS2339.
   *
   * Fix: wrap the RPC call in `Promise.resolve()` so we always deal with a
   * real `Promise` that has both `.then()` and `.catch()`.
   */
  const updateMemberBalances = async (
    memberId: string,
    duesDelta: number,
    creditDelta: number,
    fallbackDuesBalance: number,
    fallbackCreditBalance: number
  ): Promise<{ error: Error | null }> => {
    try {
      const { error } = await Promise.resolve(
        supabase.rpc('update_member_balances', {
          p_member_id: memberId,
          p_dues_delta: duesDelta,
          p_credit_delta: creditDelta,
        })
      );
      if (!error) return { error: null };
      // RPC returned an error — fall through to direct update
    } catch {
      // RPC not available — fall through to direct update
    }

    // Fallback: direct column update
    const { error: updateError } = await supabase
      .from('members')
      .update({
        dues_balance: fallbackDuesBalance,
        credit_balance: fallbackCreditBalance,
      })
      .eq('id', memberId);

    return { error: updateError ? new Error(updateError.message) : null };
  };

  const handleSavePayments = async () => {
    const selectedMembers = members.filter((m) => m.is_selected && parseFloat(m.amount) > 0);
    
    if (selectedMembers.length === 0) {
      toast.error('Please select at least one member and enter an amount');
      return;
    }

    // Validate all amounts up-front before touching the DB
    const invalidMembers: string[] = [];
    
    for (const member of selectedMembers) {
      const amount = parseFloat(member.amount);
      
      if (isNaN(amount)) {
        invalidMembers.push(`${member.full_name}: Invalid amount format`);
        continue;
      }
      if (amount < 0) {
        invalidMembers.push(`${member.full_name}: Amount cannot be negative`);
        continue;
      }
      if (amount === 0) {
        invalidMembers.push(`${member.full_name}: Amount must be greater than 0`);
      }
    }

    if (invalidMembers.length > 0) {
      toast.error(`Validation failed:\n${invalidMembers.join('\n')}`);
      return;
    }

    if (!currentOrganization) return;

    setIsSubmitting(true);

    try {
      const failedPayments: Array<{ member: string; reason: string }> = [];
      const successfulMemberIds: string[] = [];
      
      for (const member of selectedMembers) {
        try {
          const amount = parseFloat(member.amount);
          const allocation = calculateAllocation(member);

          const { data: paymentData, error: paymentError } = await supabase
            .from('payments')
            .insert({
              organization_id: currentOrganization.id,
              member_id: member.id,
              payment_type: 'dues',
              amount,
              payment_date: paymentDate,
              payment_method: paymentMethod,
              reference_number: referenceNumber || null,
              notes: notes || null,
            })
            .select()
            .single();

          if (paymentError) {
            failedPayments.push({
              member: member.full_name,
              reason: paymentError.message || 'Failed to create payment record'
            });
            continue;
          }

          const allocations = [];
          if (allocation.backlog > 0) {
            allocations.push({
              payment_id: paymentData.id,
              allocation_type: 'backlog',
              amount: allocation.backlog,
              notes: 'Payment for outstanding dues',
            });
          }
          if (allocation.current > 0) {
            allocations.push({
              payment_id: paymentData.id,
              allocation_type: 'current',
              amount: allocation.current,
              notes: `Payment for ${format(new Date(), 'MMMM yyyy')}`,
            });
          }
          if (allocation.prepay > 0) {
            allocations.push({
              payment_id: paymentData.id,
              allocation_type: 'prepay',
              amount: allocation.prepay,
              notes: 'Prepayment for future dues',
            });
          }

          if (allocations.length > 0) {
            const { error: allocError } = await supabase
              .from('payment_allocations')
              .insert(allocations);

            if (allocError) {
              failedPayments.push({
                member: member.full_name,
                reason: 'Failed to allocate payment'
              });
              continue;
            }
          }

          const { error: updateError } = await updateMemberBalances(
            member.id,
            -allocation.backlog,
            allocation.prepay,
            Math.max(0, (member.dues_balance || 0) - allocation.backlog),
            (member.credit_balance || 0) + allocation.prepay
          );

          if (updateError) {
            failedPayments.push({
              member: member.full_name,
              reason: 'Failed to update member balance'
            });
            continue;
          }

          addPayment(paymentData);
          successfulMemberIds.push(member.id);
        } catch (memberError) {
          failedPayments.push({
            member: member.full_name,
            reason: memberError instanceof Error ? memberError.message : 'Unknown error'
          });
        }
      }

      if (failedPayments.length > 0 && successfulMemberIds.length === 0) {
        const failureList = failedPayments.map(f => `${f.member}: ${f.reason}`).join('\n');
        toast.error(`Payment processing failed:\n${failureList}`);
      } else if (failedPayments.length > 0) {
        // FIX #10 (partial failure UX): Deselect & clear the members that
        // succeeded so only the failed ones remain checked and ready to retry.
        setMembers((prev) =>
          prev.map((m) =>
            successfulMemberIds.includes(m.id)
              ? { ...m, is_selected: false, amount: '' }
              : m
          )
        );
        const failureList = failedPayments.map(f => `${f.member}: ${f.reason}`).join('\n');
        toast.error(`Processed ${successfulMemberIds.length} payments but failed for:\n${failureList}`);
      } else {
        toast.success(t('dues.paymentSaved'));
        navigate('/dues');
      }

      // FIX #7: Refresh member list after any successful payment so
      // balances shown on re-entry are always up to date.
      if (successfulMemberIds.length > 0) {
        await fetchMembers();
      }
    } catch (error) {
      console.error('Error saving payments:', error);
      toast.error(error instanceof Error ? error.message : t('errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedCount = members.filter((m) => m.is_selected).length;
  const totalAmount = members
    .filter((m) => m.is_selected)
    .reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0);

  // FIX #6: Warn when monthly dues amount is not configured
  const isDuesUnconfigured = monthlyDues === 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Button variant="ghost" onClick={() => navigate('/dues')} className="mb-2">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">{t('dues.collection')}</h1>
          <p className="text-muted-foreground">
            Monthly dues: {isDuesUnconfigured ? (
              <span className="text-amber-600 font-medium">Not configured</span>
            ) : (
              `${currencySymbol}${monthlyDues}`
            )}
          </p>
        </div>
        <Button 
          onClick={handleSavePayments}
          disabled={isSubmitting || selectedCount === 0}
        >
          {isSubmitting ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Payments ({currencySymbol}{totalAmount.toLocaleString()})
        </Button>
      </div>

      {/* FIX #6: Unconfigured dues warning banner */}
      {isDuesUnconfigured && (
        <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>
            Monthly dues amount is not set. All payments will be classified as <strong>Prepay</strong> until you configure it in Settings.
          </span>
        </div>
      )}

      {/* Payment Details */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>{t('dues.paymentDate')}</Label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('dues.paymentMethod')}</Label>
              <Select value={paymentMethod} onValueChange={(v) => setPaymentMethod(v as 'cash' | 'transfer' | 'check' | 'card')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">{t('dues.cash')}</SelectItem>
                  <SelectItem value="transfer">{t('dues.transfer')}</SelectItem>
                  <SelectItem value="check">{t('dues.check')}</SelectItem>
                  <SelectItem value="card">{t('dues.card')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('dues.reference')} (Optional)</Label>
              <Input
                placeholder="Ref #"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Input
                placeholder="Any notes..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search and Summary */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t('dues.searchMembers')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-muted-foreground">
            {selectedCount} selected
          </span>
          <span className="font-medium">
            Total: {currencySymbol}{totalAmount.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Members List */}
      <div className="space-y-2">
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-20 bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        ) : filteredMembers.length > 0 ? (
          filteredMembers.map((member) => {
            const allocation = member.is_selected ? calculateAllocation(member) : null;
            return (
              <Card 
                key={member.id}
                className={member.is_selected ? 'border-primary' : ''}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <Checkbox
                      checked={member.is_selected}
                      onCheckedChange={() => toggleMember(member.id)}
                      className="h-5 w-5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{member.full_name}</p>
                        {member.dues_balance > 0 && (
                          <Badge variant="destructive" className="text-xs">
                            Owes {currencySymbol}{member.dues_balance}
                          </Badge>
                        )}
                        {member.credit_balance > 0 && (
                          <Badge variant="default" className="text-xs">
                            Credit {currencySymbol}{member.credit_balance}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{member.title}</p>
                    </div>
                    <div className="w-32">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={member.amount}
                        onChange={(e) => updateAmount(member.id, e.target.value)}
                        disabled={!member.is_selected}
                        className="text-right"
                      />
                    </div>
                  </div>
                  
                  {/* Allocation Breakdown */}
                  {member.is_selected && allocation && parseFloat(member.amount) > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                        <Info className="w-4 h-4" />
                        <span>Smart Allocation:</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {allocation.backlog > 0 && (
                          <Badge variant="destructive" className="text-xs">
                            Backlog: {currencySymbol}{allocation.backlog}
                          </Badge>
                        )}
                        {allocation.current > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            Current: {currencySymbol}{allocation.current}
                          </Badge>
                        )}
                        {allocation.prepay > 0 && (
                          <Badge variant="default" className="text-xs">
                            Prepay: {currencySymbol}{allocation.prepay}
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        ) : (
          <div className="text-center py-8">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No members found</p>
          </div>
        )}
      </div>
    </div>
  );
}
